// ─────────────────────────────────────────────────────────────────────────
// SINGLE SOURCE OF TRUTH
// The entire CV renders from this one JSON-serializable object. Swap `cvData`
// for a different object (same shape) and you get a completely different CV
// with the identical rendering pipeline. No content strings live in the
// components — only presentation logic (tech→icon lookup, pagination) does.
// ─────────────────────────────────────────────────────────────────────────

/** A bullet is either a plain string or a lead line with nested sub-points. */
export type Bullet = string | { text: string; sub?: string[] }

export interface ExperienceItem {
  title: string
  company: string
  industry?: string
  /** Short lettermark for the company monogram (fallback when no logo). */
  mark?: string
  /** Company domain used to fetch a brand logo (falls back to the monogram). */
  domain?: string
  /** Explicit logo URL — takes priority over `domain`. */
  logo?: string
  period: string
  location?: string
  summary?: string[]
  bullets?: Bullet[]
  subsections?: { heading: string; bullets: Bullet[] }[]
  /** Technologies used — plain tags; icons are resolved at render time. */
  stack?: string[]
}

export interface EducationItem {
  degree: string
  school: string
  period?: string
  note?: string
  mark?: string
  domain?: string
}

export interface EarlierRole {
  company: string
  role: string
  period: string
  note?: string
  stack?: string[]
}

export interface SkillGroup {
  label: string
  items: string[]
}

export interface CVData {
  /** Global microcopy / labels (previously hardcoded in components). */
  meta: {
    documentLabel: string // e.g. "Curriculum Vitæ"
    kicker: string // masthead role kicker
    homeCity: string // shown in masthead meta rail
    colophon: string // footer note
    exportFileSuffix: string // e.g. "CV" -> "Zak-Assali-CV"
    /** How many recent roles keep full detail; the rest are condensed. */
    detailedRoleCount: number
    sections: {
      profile: { index: string; title: string }
      capabilities: { index: string; title: string }
      experience: { index: string; title: string; continued: string }
      earlier: { index: string; title: string }
      education: { index: string; title: string }
    }
  }
  profile: {
    name: string
    headline: string
    phone: string
    email: string
    location: string
    website?: string
    linkedin?: string
    github?: string
  }
  stats: { value: string; label: string }[]
  summary: string[]
  skills: SkillGroup[]
  experience: ExperienceItem[]
  education: EducationItem[]
  earlierExperience: EarlierRole[]
}

export const cvData: CVData = {
  meta: {
    documentLabel: 'Curriculum Vitæ',
    kicker: 'Senior Frontend / Fullstack Developer',
    homeCity: 'London',
    colophon: 'Set in Fraunces & Space Grotesk',
    exportFileSuffix: 'CV',
    detailedRoleCount: 6,
    sections: {
      profile: { index: '01', title: 'Profile' },
      capabilities: { index: '02', title: 'Capabilities' },
      experience: {
        index: '03',
        title: 'Selected Experience',
        continued: 'Selected Experience — continued',
      },
      earlier: { index: '04', title: 'Earlier Experience' },
      education: { index: '05', title: 'Education' },
    },
  },

  profile: {
    name: 'Zak Assali',
    headline:
      'Senior Frontend / Fullstack Developer · JavaScript · TypeScript · React.js · Redux · AWS · UI & UX Design · Next.js · Node.js · Web3 · Gaming · Esports',
    phone: '07944 868108',
    email: 'zak@zak.media',
    location: 'London, United Kingdom',
    website: 'zak.media',
    linkedin: 'linkedin.com/in/zak-assali',
    github: 'github.com/zak-assali',
  },

  stats: [
    { value: '15+', label: 'Years shipping' },
    { value: '20+', label: 'Products delivered' },
    { value: '7', label: 'Industries' },
    { value: 'Lead', label: 'FE / UX & teams' },
  ],

  summary: [
    'I design and ship user-focused, scalable products across web and mobile — for both high-growth B2C brands and demanding enterprise B2B environments.',
    'I lead from architecture through delivery: defining frontend architecture and reusable design systems, driving UI/UX decisions, and mentoring and hiring the teams that build alongside me. My work spans fintech and banking (BNP Paribas trading platforms, ClearBank, uSwitch), healthcare (Orri Clinic, Cera Care), government and research (Department for Business & Trade, UKRI), web3 and crypto (XCAD, showcased in Times Square), and gaming & esports (ESL FACEIT Group).',
    'I specialise in modern React, Next.js, TypeScript and React Native, pairing engineering rigour — TDD, accessibility (WCAG AA), GDS standards, performance — with genuine design craft, engineering everything from bespoke calendar and scheduling engines to data-heavy analytics interfaces and globally-used mobile apps (Shell, serving millions of drivers).',
    'Whether greenfield or legacy modernisation, I turn complex problems into polished, maintainable products — and enjoy being the bridge between product, design and engineering.',
  ],

  skills: [
    {
      label: 'Languages',
      items: [
        'TypeScript',
        'JavaScript',
        'ES6+',
        'HTML5',
        'CSS3',
        'SCSS',
        'LESS',
        'Python',
        'C#',
        'PHP',
        'SQL',
      ],
    },
    {
      label: 'Frontend & UI',
      items: [
        'React',
        'Next.js',
        'React Native',
        'Redux',
        'Redux Toolkit',
        'Recoil',
        'React Query',
        'Angular.js',
        'Tailwind CSS',
        'Chakra UI',
        'MUI5',
        'Styled System',
        'Styled Components',
        'Bootstrap',
        'AG Grid',
        'Ionic',
      ],
    },
    {
      label: 'Backend & Cloud',
      items: [
        'Node.js',
        'Strapi',
        '.NET Framework',
        'ASP.NET',
        'AWS',
        'Amplify',
        'Lambda',
        'S3',
        'DynamoDB',
        'EC2',
        'Cognito',
        'Vercel',
        'REST APIs',
        'Blockchain',
        'Zilliqa',
      ],
    },
    {
      label: 'Tooling & Testing',
      items: [
        'Vite',
        'Webpack',
        'Gulp',
        'Playwright',
        'Figma',
        'WordPress',
        'Drupal',
        'Chrome Extension',
      ],
    },
    {
      label: 'Practices & Standards',
      items: [
        'Test-Driven Development',
        'Trunk-Based Development',
        'Mob Programming',
        'GDS',
        'WCAG AA Accessibility',
        'Mobile App Development',
      ],
    },
  ],

  experience: [
    {
      title: 'Senior Frontend Engineer (Contract)',
      company: 'Uswitch',
      industry: 'Fintech · Price Comparison',
      mark: 'US',
      domain: 'uswitch.com',
      period: '11/2025 – 03/2026',
      location: 'London, United Kingdom',
      summary: [
        "Designed and built uSwitch's B2B analytics platform from the ground up, delivering a scalable, high-performance data interface used by internal commercial and partner teams.",
      ],
      bullets: [
        'Led frontend architecture and implementation for a greenfield B2B analytics product.',
        'Built complex data-heavy views using React, TypeScript, React Query, and modern component patterns.',
        'Designed and implemented a reusable UI system using Tailwind CSS and shadcn/ui.',
        'Integrated analytics APIs with efficient caching, pagination, and state management strategies.',
        'Collaborated closely with product, data, and backend teams to shape requirements and delivery.',
        'Delivered production-ready features under tight timelines within a contract engagement.',
      ],
      stack: [
        'React',
        'TypeScript',
        'React Query',
        'Tailwind CSS',
        'shadcn/ui',
        'Vite',
        'REST APIs',
      ],
    },
    {
      title: 'Lead Front End Developer & Lead UI/UX Designer',
      company: 'Orri Clinic — Health Care',
      industry: 'Healthcare',
      mark: 'OR',
      domain: 'orri-uk.com',
      period: '08/2024 – 11/2025',
      location: 'London, England, United Kingdom · Remote',
      summary: [
        'Led the development and design of a comprehensive digital ecosystem for Orri Clinic, a specialized healthcare provider, consisting of two interconnected applications.',
      ],
      subsections: [
        {
          heading: 'Staff Platform',
          bullets: [
            'Developed an advanced healthcare management platform enabling clinical staff to coordinate care delivery.',
            'Engineered a bespoke calendar and scheduling system from scratch — grid-based event rendering, custom overlap detection, complex recurrence logic, and timezone-aware date/time components.',
            'Implemented real-time data visualization components for operational insights',
            'Created intuitive interfaces for managing team members, clients, and appointments',
          ],
        },
        {
          heading: 'Client Platform',
          bullets: [
            'Engineered a patient-facing application providing seamless access to care information',
            'Integrated secure video conferencing capabilities for remote consultations',
            'Developed personalized scheduling interfaces for clients to manage appointments',
          ],
        },
      ],
      stack: [
        'TypeScript',
        'React',
        'Tailwind CSS',
        'React Context API',
        'Playwright',
        'AWS',
        'Amplify',
        'Lambda',
        'DynamoDB',
      ],
    },
    {
      title: 'Senior Front End Engineer & UI Designer',
      company: 'BNP Paribas',
      industry: 'Investment Banking',
      mark: 'BNP',
      domain: 'bnpparibas.com',
      period: '02/2022 – 06/2022',
      location: 'London',
      summary: [
        'As a senior developer in the financial sector, played a key role in modernising front office trading platforms by leading the migration from legacy WebForms interfaces to a React-based front-end architecture, designing scalable, reusable UI components and robust user workflows for a dynamic trading environment.',
      ],
      bullets: [
        'Defined front-end architecture and user interaction patterns to enhance performance and maintainability.',
        'Led the development and migration process, ensuring seamless integration with existing backend systems.',
        'Conducted code reviews to uphold high development standards and foster team collaboration.',
        'Executed rigorous testing and debugging protocols, providing ongoing support to ensure successful adoption.',
      ],
      stack: [
        'AG Grid',
        'React',
        'Redux',
        'ES6+',
        'Webpack',
        'HTML5',
        'CSS3',
        '.NET Framework',
        'WebForms',
      ],
    },
    {
      title: 'Senior Frontend Developer',
      company: 'Cera Care',
      industry: 'Healthcare',
      mark: 'CC',
      domain: 'ceracare.co.uk',
      period: '02/2021 – 01/2022',
      location: 'London · Remote',
      summary: [
        'As a senior developer in a healthcare environment, primarily engaged on the design, development, testing and deployment of both internal and client-facing apps for mobile and web, defining technology architecture and user flows to support a high-volume facility, and developing reusable UI components to drive scalability.',
      ],
      bullets: [
        'Supported peer reviews to maintain development standards',
        'Performed comprehensive testing procedures and provided ongoing troubleshooting and change management to reduce barriers to adoption',
        'Nurtured a continuous improvement mindset towards both status quo technology and processes',
      ],
      stack: [
        'React',
        'Recoil',
        'React Query',
        'ES6+',
        'MUI5',
        'Styled System',
        'HTML5+',
      ],
    },
    {
      title: 'Interim Chief Technology Officer',
      company: 'Dermassal Clinics',
      industry: 'Medical Cosmetics',
      mark: 'DC',
      domain: 'dermassalclinics.com',
      logo: 'https://cdn.magicpatterns.com/uploads/nUKE2EqhxN1ye29t5ZuSKu/pasted-image.jpg',
      period: '01/2021 – 12/2021',
      location: 'London, United Kingdom',
      summary: [
        'Partnered with a premium medical cosmetic clinic to spearhead the development of a bespoke solution that revolutionized all administrative activities, including scheduling, communications, client relations, and invoicing. As lead architect, ensured technical feasibility and rigorous regulatory compliance of the entire tech stack and business decisions.',
        'Effectively coordinated projects and milestones, ensuring timely delivery and adherence to budget constraints despite working with a distributed team across multiple time zones.',
      ],
      bullets: [
        'Assembled and managed a remote team of developers and designers, fostering a collaborative and productive work environment.',
        'Led both business and digital development, incorporating captivating animations and transitions.',
        'Created a new CMS to streamline content management and improve efficiency.',
        "Successfully integrated third-party APIs for social media, telecom, and payment providers, enhancing the clinic's online presence and enabling seamless transactions.",
      ],
      stack: [
        'React',
        'Next.js',
        'TypeScript',
        'AWS',
        'ES6+',
        'Vercel',
        'Figma',
      ],
    },
    {
      title: 'Senior Frontend Developer',
      company: 'ESL FACEIT Group — EFG',
      industry: 'Gaming · Esports',
      mark: 'EFG',
      domain: 'eslfaceitgroup.com',
      period: '06/2023 – 06/2024',
      location: 'London, England, United Kingdom · Remote',
      summary: [
        'As a Senior Front-End Developer at EFG Faceit, a prominent esports organisation, specialised in leveraging Next.js to create dynamic and engaging web applications, working closely with cross-functional teams to deliver high-quality front-end solutions.',
      ],
      bullets: [
        'Developed and maintained advanced front-end web applications using Next.js, making use of newer features such as the app router, dynamic image generation leveraging Open Graph, React server components and server actions.',
        'Collaborated with UX/UI designers to transform complex designs into responsive, interactive web pages.',
        'Optimized applications for maximum performance, ensuring smooth and fast load times.',
        'Ensured cross-browser compatibility and maintained high standards of user experience.',
        'Implemented modern JavaScript practices and stayed abreast of the latest industry trends.',
      ],
      stack: [
        'Next.js',
        'React',
        'TypeScript',
        'ES6+',
        'React Query',
        'Open Graph',
        'CSS3',
        'Node.js',
      ],
    },
    {
      title: 'JavaScript Full-stack Developer',
      company: 'Worth',
      industry: 'Government · Research',
      mark: 'W',
      domain: 'worth.systems',
      period: '01/2023 – 04/2023',
      location: 'London, England, United Kingdom · Remote',
      summary: [
        "As a Fullstack developer, served as a pivotal member of Worth UK's team, developing a cutting-edge platform for the UKRI department, a vital government entity dedicated to fostering innovation and research — a high-stakes undertaking requiring precise planning, technical expertise, and adaptability.",
        'Played a central part in designing and implementing the full tech stack, applying skills in frontend and backend JavaScript technologies, as well as best practices in test-driven development and Mob programming.',
      ],
      bullets: [
        'Contributed to the construction of a critical platform that significantly improved the operational efficiency of UKRI',
        'Applied test-driven development, Mob programming, and trunk-based development to improve code quality and collaboration',
      ],
      stack: [
        'React',
        'Next.js',
        'TypeScript',
        'Node.js',
        'AWS',
        'Amplify',
        'Lambda',
        'DynamoDB',
        'EC2',
        'GDS',
        'Mob Programming',
        'Trunk-Based Development',
        'Test-Driven Development',
      ],
    },
    {
      title: 'Senior Front-End Blockchain Developer',
      company: 'XCAD Network',
      industry: 'Web3 · Crypto',
      mark: 'XC',
      domain: 'xcadnetwork.com',
      period: '06/2022 – 12/2022',
      location: 'London, England, United Kingdom · Remote',
      summary: [
        "As a senior frontend developer in the crypto space, worked for XCAD Token to build out their flagship Chrome extension. With backing from some of the world's biggest influencers including Mr. Beast and KSI, XCAD leveraged blockchain technology to allow users to earn tokens and govern content creator decisions via a Chrome plugin.",
        'Shaped the technology architecture with ownership of key tech stack, storage patterns, testing, and UI/UX decisions, and was the primary driver behind the integration of the payment processor and the Zilliqa API for token trading, staking, balance tracking and wallet storage.',
      ],
      bullets: [
        'Shipped a Chrome plugin MVP within a strict deadline that was ultimately showcased at Times Square',
        'Built a reusable component library to promote scale-up and consistency',
      ],
      stack: [
        'React',
        'Next.js',
        'TypeScript',
        'ES6+',
        'Chrome Extension',
        'Ionic',
        'Node.js',
        'Styled System',
        'React Query',
        'Blockchain',
        'Zilliqa',
        'AWS',
        'Amplify',
        'Lambda',
        'DynamoDB',
        'Cognito',
      ],
    },
    {
      title: 'Front End Lead',
      company: 'Instinct Digital',
      industry: 'Fintech · Agency',
      mark: 'ID',
      domain: 'instinctdigital.co.uk',
      period: '11/2019 – 08/2020',
      location: 'London, England, United Kingdom · On-site',
      summary: [
        'At Instinct Studios, served as the lead front-end developer in a fast-paced, rapid-release Fintech agency.',
        'Collaborated with the CEO and project team to design and deliver an asset class analytics platform UI, featuring a map-based interface and leveraging Machine Learning to forecast asset management trends.',
        'In a client-facing role for The Bank of New York Mellon, provided weekly status updates and reports, gathered detailed requirements, and communicated key insights and feedback to the Product and Engineering teams.',
      ],
      bullets: [
        'Promoted to Lead Developer from Senior Developer in 1 month',
        'Trained and mentored new developers',
      ],
      stack: [
        'React',
        'ES6+',
        'Strapi',
        'TypeScript',
        'Chakra UI',
        'Node',
        'Styled System',
        'HTML5+',
      ],
    },
    {
      title: 'Senior Frontend Developer',
      company: 'Department for Business and Trade',
      industry: 'Government',
      mark: 'DBT',
      domain: 'gov.uk',
      period: '01/2019 – 09/2019',
      location: 'London, England, United Kingdom · On-site',
      summary: [
        'Directed the implementation of a CRM platform using defined software development standards, including regular code reviews to navigate roadblocks and assure compliance with accepted best practice, as well as the development of training materials and reusable template libraries.',
      ],
      bullets: [
        'Met WCAG AA accessibility standards',
        'Adhered to government GDS standards for building web platforms',
        'Adhered to strict delivery timelines including 48 hours to delivery',
      ],
    },
    {
      title: 'Front End Lead',
      company: 'Josh Wood Colour',
      industry: 'E-commerce · Beauty',
      mark: 'JW',
      domain: 'joshwoodcolour.com',
      period: '08/2018 – 02/2019',
      location: 'London, England, United Kingdom · Remote',
      summary: ['Cosmetics company'],
      bullets: [
        'Partnered with the client to develop the storefront, and design new enhancements and features on the roadmap for a rapid-growth e-commerce venture.',
        'Created a React based chat bot that would recommend products to customers based on the conversation.',
      ],
      stack: ['React', 'JavaScript', 'ES6+', 'HTML5', 'SCSS', 'REST APIs'],
    },
    {
      title: 'React Native Developer',
      company: 'Shell',
      industry: 'Energy',
      mark: 'SH',
      domain: 'shell.com',
      period: '09/2017 – 08/2018',
      location: 'London, England, United Kingdom · On-site',
      summary: [
        'At Shell, was part of a team that built a client-facing app used by millions of drivers worldwide for both iOS and Android. As a member of the Development team, collaborated with backend and frontend developers on the frontend of the app.',
      ],
      bullets: [
        'Integrated and structured the RESTful API',
        'Hired, trained and managed the recruitment of top talent',
      ],
      stack: ['React Native', 'Redux', 'Styled Components'],
    },
    {
      title: 'Front End Lead',
      company: 'ClearBank',
      industry: 'Banking',
      mark: 'CB',
      domain: 'clear.bank',
      period: '10/2016 – 09/2017',
      location: 'London, England, United Kingdom · On-site',
      summary: [
        "Given the challenger bank's relative digital nascence, was given a wide scope of ownership in revamping the UI/UX interface and front-end code base. Collaborating with backend developers, led the development of architectural solutions that facilitated seamless integration between back and front-end code bases with no undue disruption to the organization.",
        'At an internal level, was a strong advocate of automation to streamline legacy processes and unlock recurring operating efficiencies. Also led the hiring, training and mentorship of developers to promote capacity-building and develop high-quality talent.',
      ],
      bullets: [
        'Built a library of reusable and scalable web components that integrated with the back-end of different portals without disruption',
        'Upgraded legacy code and CSS technologies/patterns to revamp the entirety of the frontend code base',
      ],
      stack: ['JavaScript', 'ES6+', 'HTML5', 'CSS3', 'SCSS', 'REST APIs'],
    },
    {
      title: 'Frontend Developer',
      company: 'Lebara',
      industry: 'Telecoms',
      mark: 'LB',
      domain: 'lebara.com',
      period: '10/2015 – 03/2016',
      location: 'London, England, United Kingdom · On-site',
      summary: ['Telco'],
      bullets: [
        'Partnered with technical and design teams to revamp a website, and was the lead UI developer',
        'Accountable for recommending a new tech stack, forming build tools and resources',
        'Maintained updated status reporting for performance measurement purposes',
      ],
      stack: ['JavaScript', 'HTML5', 'SCSS', 'Bootstrap', 'Node.js', 'Gulp'],
    },
    {
      title: 'Frontend Developer',
      company: 'Lloyds Banking Group',
      industry: 'Banking',
      mark: 'LL',
      domain: 'lloydsbankinggroup.com',
      period: '10/2015 – 03/2016',
      location: 'London, England, United Kingdom · On-site',
      summary: ['Banking'],
      bullets: [
        'Delivered responsive single-page application journeys using OO-JavaScript / React.js and flux architecture that connected and communicated with a range of REST APIs.',
        'Developed frontend code and automated tests while following Agile project principles and methodologies.',
      ],
      stack: ['React', 'JavaScript', 'ES6+', 'HTML5', 'CSS3', 'REST APIs'],
    },
  ],

  education: [
    {
      degree: 'BTEC National Diploma — Software Development',
      school: 'Westminster Kingsway College',
      note: 'Grade: Triple Distinction (AAA equivalent)',
      mark: 'WK',
    },
    {
      degree: 'Microsoft Certified Professional',
      school: '.NET / C#',
      note: 'Professional certification',
      mark: 'MS',
    },
    {
      degree: 'European Computer Driving Licence',
      school: 'ECDL',
      note: 'Certified',
      mark: 'EC',
    },
  ],

  earlierExperience: [
    {
      company: 'Republik Media',
      role: 'Lead Frontend & WordPress Developer',
      period: '2014',
      note: 'Led a team of 4 delivering interactive, responsive websites from the ground up',
      stack: [
        'HTML5',
        'CSS3',
        'jQuery',
        'WordPress',
        'Bootstrap',
        'Angular.js',
      ],
    },
    {
      company: 'Spinach Design',
      role: 'Lead Frontend & WordPress Developer',
      period: '2014',
      note: 'Sole frontend/WordPress developer building fully responsive, client-editable sites from scratch',
      stack: ['HTML5', 'CSS3', 'jQuery', 'WordPress', 'Bootstrap'],
    },
    {
      company: 'ASOS',
      role: 'Ecommerce Frontend / Email Developer',
      period: '2013 – 2014',
      note: 'Responsive emails and web pages for the marketing team; maintained the Sitecore ecommerce CMS',
      stack: ['HTML5', 'CSS3', 'JavaScript', 'Sitecore'],
    },
    {
      company: 'Steak Digital',
      role: 'Frontend / WordPress Developer',
      period: '2013',
      note: 'Responsive WordPress blog for the Parcelforce website',
      stack: ['WordPress', 'HTML5', 'CSS3', 'PHP'],
    },
    {
      company: 'M-Integrated Solutions',
      role: 'Contract Frontend / Email / WordPress Developer',
      period: '2013',
      note: 'Three projects across CMS re-styling, responsive emails, and portfolio sites',
      stack: ['HTML5', 'CSS3', 'jQuery', 'PHP', 'WordPress'],
    },
    {
      company: 'Method',
      role: 'Responsive Frontend Developer',
      period: '2013',
      note: 'Responsive banking website plus a component style-guide/sandbox app',
      stack: ['HTML5', 'LESS', 'SASS', 'Node.js', 'Bootstrap'],
    },
    {
      company: 'VCCP London',
      role: 'Responsive Frontend Developer',
      period: '2013',
      note: 'Frontend for the Saga website and HTML5 interactive banners for ASDA',
      stack: ['HTML5', 'CSS3', 'jQuery', 'LESS', 'SASS'],
    },
    {
      company: 'Rockit Bootcamp',
      role: 'Development Instructor',
      period: '2013',
      note: 'Evening instructor teaching interactive, dynamic web development',
      stack: ['HTML5', 'CSS3', 'JavaScript', 'jQuery'],
    },
    {
      company: 'Capgemini UK',
      role: 'Developer',
      period: '2011 – 2012',
      note: 'Royal Mail Group site redesign on Drupal; a centralised .NET system for 6 London councils',
      stack: ['C#', 'ASP.NET', 'SQL', 'Drupal', 'HTML5', 'CSS3', 'JavaScript'],
    },
    {
      company: 'Loophole Studios',
      role: 'Web Developer',
      period: '2009 – 2011',
      note: 'Website & logo design, PPC, SEO, and social/mobile marketing',
      stack: ['HTML5', 'CSS3', 'jQuery', 'PHP'],
    },
    {
      company: 'Sheriff Ratings Ltd',
      role: 'Junior Web Developer',
      period: '2008 – 2009',
      note: 'Website & logo design, PPC and SEO',
      stack: ['HTML5', 'CSS3', 'jQuery'],
    },
  ],
}

export default cvData
